<template>
  <div class="sb-accordion" :class="activeClasses">
    <button
      class="sb-accordion__button"
      type="button"
      :aria-expanded="isOpenLocal"
      :aria-controls="`collapse${title}`"
      @click="toggleAccordion"
    >
      <SbIcon :name="chevronIcon" />
      <p class="sb-accordion__title">
        {{ title }}
      </p>

      <SbIcon
        v-if="icon"
        v-tooltip="{ label: iconDescription }"
        :name="icon"
        class="sb-accordion__icon"
        @click.stop="$emit('icon-click')"
      />

      <div v-if="$slots.contentInfo" class="sb-accordion__content-info">
        <slot name="contentInfo" />
      </div>
    </button>

    <AccordionTransition>
      <div
        v-show="isOpenLocal"
        :id="`collapse${title}`"
        class="sb-accordion__content"
      >
        <slot />
      </div>
    </AccordionTransition>
  </div>
</template>

<script lang="ts" setup>
import { computed, watch, ref } from 'vue'
import SbIcon from '../Icon'
import type { IconName } from '../Icon'
import AccordionTransition from './AccordionTransition.vue'
import { Tooltip as vTooltip } from '../../directives'

export type SbAccordionProps = {
  icon?: IconName
  iconDescription?: string
  isOpen?: boolean
  noPadding?: boolean
  noHighlight?: boolean
  noBorder?: boolean
  title: string
}

const props = withDefaults(defineProps<SbAccordionProps>(), {
  icon: undefined,
  iconDescription: undefined,
  isOpen: false,
  noPadding: false,
  noHighlight: false,
  noBorder: false,
})

export type SbAccordionEmits = {
  (e: 'toggle-open', value: boolean): void
  (e: 'icon-click'): void
}

const emit = defineEmits<SbAccordionEmits>()

const isOpenLocal = ref(false)

watch(
  () => props.isOpen,
  (newValue) => {
    isOpenLocal.value = newValue
  },
  { immediate: true },
)

function toggleAccordion(): void {
  isOpenLocal.value = !isOpenLocal.value
  emit('toggle-open', isOpenLocal.value)
}

const chevronIcon = computed<IconName>(() =>
  isOpenLocal.value ? 'chevron-down' : 'chevron-right',
)

const activeClasses = computed(() => ({
  'sb-accordion--open': isOpenLocal.value,
  'sb-accordion--no-highlight': props.noHighlight,
  'sb-accordion--no-padding': props.noPadding,
  'sb-accordion--no-border': props.noBorder,
}))
</script>
